import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import { EmptyState } from "../ui/empty-state";
import { Field } from "../ui/field";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { PanelHeader } from "./PanelHeader";
import { ExtractToolbar } from "./ExtractToolbar";
import { RosterGuidePanel } from "./RosterGuidePanel";
import { cn } from "../../lib/utils";
import {
  ARC_LEVER_CATEGORIES,
  ARC_LEVER_LABELS,
  createBlankLevers,
  finalLeverScores,
  leverSeriesForCharacter,
  sortCharactersMajorFirst,
  type BookCharacter,
  type CharacterRemovalRecord,
  type CharacterRole,
  type ArcLeverCategory,
} from "../../../shared/characterRoster";

const SERIES_COLORS: Record<ArcLeverCategory, string> = {
  baseline: "#64748b",
  motivation: "#3b82f6",
  behavior: "#a855f7",
  crucible: "#ef4444",
  resolution: "#22c55e",
};

function LeverGraph({
  character,
  chapters,
}: {
  character: BookCharacter;
  chapters: Array<{ id: string; order: number; title: string }>;
}) {
  const series = leverSeriesForCharacter(character, chapters);
  const [focus, setFocus] = useState<ArcLeverCategory>("resolution");

  if (character.role !== "major") {
    return (
      <EmptyState compact title="Minor characters do not track arc levers." />
    );
  }
  if (series.length === 0) {
    return (
      <EmptyState
        compact
        title="No lever scores yet."
        description="Extract or edit levers on a chapter Characters tab."
      />
    );
  }

  const width = 560;
  const height = 180;
  const padL = 36;
  const padR = 16;
  const padT = 16;
  const padB = 36;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;
  const n = series.length;
  const xAt = (i: number) =>
    padL + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yAt = (score: number) => padT + plotH - ((score - 1) / 4) * plotH;

  const finals = finalLeverScores(character, chapters);

  return (
    <div className="mt-1.5 flex flex-col gap-2.5">
      <div className="flex flex-wrap gap-1.5">
        {ARC_LEVER_CATEGORIES.map((cat) => (
          <Button
            key={cat}
            type="button"
            variant="outline"
            size="xs"
            className={cn(
              "h-auto rounded-full px-2 py-1 text-[0.7rem]",
              focus === cat && "border-primary/45 bg-primary/10",
            )}
            onClick={() => setFocus(cat)}
          >
            <span
              className="inline-block size-2 rounded-full"
              style={{ background: SERIES_COLORS[cat] }}
            />
            {ARC_LEVER_LABELS[cat]}
          </Button>
        ))}
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full max-w-140 rounded-md border border-border bg-muted/15"
        role="img"
        aria-label="Arc lever scores across chapters"
      >
        {[1, 2, 3, 4, 5].map((score) => (
          <g key={score}>
            <line
              x1={padL}
              x2={width - padR}
              y1={yAt(score)}
              y2={yAt(score)}
              stroke="var(--border)"
              strokeWidth={1}
            />
            <text
              x={padL - 8}
              y={yAt(score) + 3}
              textAnchor="end"
              fontSize={10}
              fill="var(--muted-foreground)"
            >
              {score}
            </text>
          </g>
        ))}
        {ARC_LEVER_CATEGORIES.map((cat) => {
          const points = series
            .map((pt, i) => {
              const lever = pt.levers.find((l) => l.category === cat);
              if (!lever) return null;
              return `${xAt(i)},${yAt(lever.score)}`;
            })
            .filter(Boolean)
            .join(" ");
          const muted = focus !== cat;
          return (
            <polyline
              key={cat}
              fill="none"
              stroke={SERIES_COLORS[cat]}
              strokeWidth={muted ? 1.5 : 2.5}
              opacity={muted ? 0.25 : 1}
              points={points}
            />
          );
        })}
        {series.map((pt, i) => {
          const lever = pt.levers.find((l) => l.category === focus);
          if (!lever) return null;
          return (
            <circle
              key={pt.chapterId}
              cx={xAt(i)}
              cy={yAt(lever.score)}
              r={4}
              fill={SERIES_COLORS[focus]}
            />
          );
        })}
        {series.map((pt, i) => (
          <text
            key={`label-${pt.chapterId}`}
            x={xAt(i)}
            y={height - 10}
            textAnchor="middle"
            fontSize={9}
            fill="var(--muted-foreground)"
          >
            {pt.chapterTitle.length > 12
              ? `${pt.chapterTitle.slice(0, 11)}…`
              : pt.chapterTitle}
          </text>
        ))}
      </svg>
      <div>
        <span className="text-xs font-medium">Ends at</span>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {finals.map((lever) => (
            <Badge
              key={lever.category}
              variant="secondary"
              className="gap-1 text-xs"
            >
              <span
                className="inline-block size-2 rounded-full"
                style={{ background: SERIES_COLORS[lever.category] }}
              />
              {ARC_LEVER_LABELS[lever.category]} {lever.score}
            </Badge>
          ))}
        </div>
      </div>
      <div className="mt-1 flex flex-col gap-2">
        <div className="text-xs font-medium">
          {ARC_LEVER_LABELS[focus]} evidence by chapter
        </div>
        {series.map((pt) => {
          const lever = pt.levers.find((l) => l.category === focus);
          return (
            <Card
              key={pt.chapterId}
              size="sm"
              className="gap-1 rounded-md px-2 py-1.5 ring-1 ring-border"
            >
              <strong className="text-xs">{pt.chapterTitle}</strong>
              <span className="text-xs text-muted-foreground">
                {" "}
                · score {lever?.score ?? "—"}
              </span>
              <p className="mt-1 text-xs">
                {lever?.evidence?.trim() || "No evidence noted."}
              </p>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function CharacterDetail({
  character,
  chapters,
  onUpdate,
  onRequestRemove,
  onOpenChapter,
}: {
  character: BookCharacter;
  chapters: Array<{ id: string; order: number; title: string }>;
  onUpdate: (id: string, patch: Partial<BookCharacter>) => void;
  onRequestRemove: (character: BookCharacter) => void;
  onOpenChapter: (chapterId: string) => void;
}) {
  const chapterMeta = useMemo(() => {
    const map = new Map(chapters.map((c) => [c.id, c]));
    return [...character.appearances]
      .map((a) => map.get(a.chapterId))
      .filter((c): c is { id: string; order: number; title: string } =>
        Boolean(c),
      )
      .sort((a, b) => a.order - b.order);
  }, [character.appearances, chapters]);

  function setRole(role: CharacterRole): void {
    if (role === "major") {
      const appearances = character.appearances.map((a) => ({
        ...a,
        levers: a.levers?.length ? a.levers : createBlankLevers(),
      }));
      onUpdate(character.id, { role, appearances });
    } else {
      const appearances = character.appearances.map(({ chapterId, note }) => ({
        chapterId,
        ...(note ? { note } : {}),
      }));
      onUpdate(character.id, { role, appearances });
    }
  }

  return (
    <div className="flex max-w-208 flex-col gap-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label="Character name"
          className="min-w-48 flex-1 text-[1.05rem] font-semibold"
          value={character.name}
          onChange={(e) => onUpdate(character.id, { name: e.target.value })}
        />
        <Select
          value={character.role}
          onValueChange={(value) => setRole(value as CharacterRole)}
        >
          <SelectTrigger size="default" aria-label="Character role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="major">Major</SelectItem>
            <SelectItem value="minor">Minor</SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="secondary"
          type="button"
          onClick={() => onRequestRemove(character)}
        >
          Remove
        </Button>
      </div>

      <Field label="Aliases (comma separated)">
        <Input
          aria-label="Aliases"
          value={character.aliases.join(", ")}
          onChange={(e) =>
            onUpdate(character.id, {
              aliases: e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
        />
      </Field>

      <Field label="Who they are (book) — lasting identity, not one-scene costume or mood">
        <Textarea
          aria-label="Description"
          rows={4}
          placeholder="Role, relationships, standing traits…"
          value={character.description}
          onChange={(e) =>
            onUpdate(character.id, { description: e.target.value })
          }
        />
      </Field>

      <section>
        <h3 className="text-sm font-medium">Appears in</h3>
        {chapterMeta.length === 0 ? (
          <EmptyState compact className="mt-1.5" title="Not linked to any chapter yet." />
        ) : (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {chapterMeta.map((ch) => (
              <Button
                key={ch.id}
                type="button"
                variant="outline"
                size="xs"
                className="rounded-full"
                onClick={() => onOpenChapter(ch.id)}
              >
                {ch.title}
              </Button>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="text-sm font-medium">Arc levers across the book</h3>
        <LeverGraph character={character} chapters={chapters} />
      </section>
    </div>
  );
}

export function CharactersGuidePanel({
  roster,
  removals,
  chapters,
  selectedId,
  onSelect,
  onUpdate,
  onRequestRemove,
  onAdd,
  holisticBusy,
  extractModel,
  onExtractModelChange,
  openAiModels,
  anthropicModels,
  onAnalyzeWholeBook,
  onOpenChapter,
}: {
  roster: BookCharacter[];
  removals: CharacterRemovalRecord[];
  chapters: Array<{ id: string; order: number; title: string }>;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onUpdate: (id: string, patch: Partial<BookCharacter>) => void;
  onRequestRemove: (character: BookCharacter) => void;
  onAdd: (role: CharacterRole) => void;
  holisticBusy: boolean;
  extractModel: string;
  onExtractModelChange: (
    model: string,
    provider: "openai" | "anthropic",
  ) => void;
  openAiModels: ReadonlyArray<{ value: string; label: string }>;
  anthropicModels: ReadonlyArray<{ value: string; label: string }>;
  onAnalyzeWholeBook: () => void;
  onOpenChapter: (chapterId: string) => void;
}) {
  const sorted = sortCharactersMajorFirst(roster);

  return (
    <RosterGuidePanel
      ariaLabel="Characters guide"
      rosterAriaLabel="Character list"
      items={sorted}
      selectedId={selectedId}
      onSelect={onSelect}
      removals={removals}
      rosterEmpty="No characters yet. Extract from a chapter or add one here."
      detailFallback="Select a character to view their page."
      header={
        <PanelHeader
          title="Characters"
          description="Book roster with aliases, chapter presence, and major-character arc levers."
          actions={
            <>
              <ExtractToolbar
                model={extractModel}
                onModelChange={onExtractModelChange}
                openAiModels={openAiModels}
                anthropicModels={anthropicModels}
                onExtract={onAnalyzeWholeBook}
                extractBusy={holisticBusy}
                extractLabel="Analyze whole book"
                extractBusyLabel="Analyzing whole book…"
              />
              <Button size="sm" type="button" onClick={() => onAdd("major")}>
                Add major
              </Button>
              <Button
                size="sm"
                variant="secondary"
                type="button"
                onClick={() => onAdd("minor")}
              >
                Add minor
              </Button>
            </>
          }
        />
      }
      renderRosterItem={(character) => (
        <>
          <span className="flex min-w-0 items-center gap-1.5 text-[0.85rem] font-semibold">
            {character.color ? (
              <span
                className="inline-block size-3.5 shrink-0 rounded-sm border border-foreground/20"
                style={{ backgroundColor: character.color.highlight }}
                title="Speaker highlight color"
                aria-hidden
              />
            ) : null}
            <span className="truncate">{character.name}</span>
          </span>
          <Badge
            variant={character.role === "major" ? "default" : "secondary"}
            className="h-4 px-1.5 text-[0.65rem] uppercase tracking-[0.04em]"
          >
            {character.role}
          </Badge>
          <span className="col-span-2 text-xs text-muted-foreground">
            {character.appearances.length} ch
            {character.description ? " · " : ""}
            {character.description ? (
              <>
                {character.description.slice(0, 80)}
                {character.description.length > 80 ? "…" : ""}
              </>
            ) : null}
          </span>
        </>
      )}
      renderDetail={(character) => (
        <CharacterDetail
          character={character}
          chapters={chapters}
          onUpdate={onUpdate}
          onRequestRemove={onRequestRemove}
          onOpenChapter={onOpenChapter}
        />
      )}
    />
  );
}
